import client from "./client.jsx";
import { tvApi as newTvApi } from "./tvApi.jsx";

// 인증 관련 API
export const authApi = {
  // 로그인 페이지 초기화 (RSA 공개키 받기)
  initLogin: async () => {
    const response = await client.post("/api/auth/initLogin");
    return response.data;
  },

  // 로그인
  login: async (credentials) => {
    const response = await client.post("/api/auth/login", credentials);
    return response.data;
  },

  // 로그아웃
  logout: async () => {
    const response = await client.post("/api/auth/logout");
    return response.data;
  },

  // 토큰 새로고침
  refresh: async () => {
    const response = await client.post("/api/auth/refresh");
    return response.data;
  },

  // 현재 사용자 정보 조회
  getCurrentUser: async () => {
    const response = await client.get("/api/auth/me");
    return response.data;
  },
};

// TV 관련 API (기존 + 새로운 인증 기능)
export const tvApi = {
  // 기존 API들
  getTVs: async () => {
    try {
      const response = await client.get("/api/tvs");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "현황판 목록 조회에 실패했습니다."
      );
    }
  },

  getTV: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "현황판 조회에 실패했습니다."
      );
    }
  },

  createTV: async (tvData) => {
    try {
      const response = await client.post("/api/tvs", tvData);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "현황판 생성에 실패했습니다."
      );
    }
  },

  updateTV: async (tvId, tvData) => {
    try {
      const response = await client.post(`/api/tvs/${tvId}`, tvData);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "현황판 수정에 실패했습니다."
      );
    }
  },

  deleteTV: async (tvId) => {
    try {
      const response = await client.delete(`/api/tvs/${tvId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "현황판 삭제에 실패했습니다."
      );
    }
  },

  // TV 설정 조회
  getTVSettings: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}/settings`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "설정 조회에 실패했습니다."
      );
    }
  },

  // TV 설정 수정
  updateTVSettings: async (tvId, settings) => {
    try {
      const response = await client.put(`/api/tvs/${tvId}/settings`, settings);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "설정 수정에 실패했습니다."
      );
    }
  },

  // 새로운 백엔드 API와 매칭
  ...newTvApi,
};

// 컨텐츠 관련 API
export const contentApi = {
  // 컨텐츠 목록 조회
  getContents: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}/contents`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "컨텐츠 목록 조회에 실패했습니다."
      );
    }
  },

  // 컨텐츠 생성
  createContent: async (tvId, contentData) => {
    try {
      const response = await client.post(
        `/api/tvs/${tvId}/contents`,
        contentData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "컨텐츠 생성에 실패했습니다."
      );
    }
  },

  // 컨텐츠 수정
  updateContent: async (tvId, contentId, contentData) => {
    try {
      const response = await client.put(
        `/api/tvs/${tvId}/contents/${contentId}`,
        contentData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "컨텐츠 수정에 실패했습니다."
      );
    }
  },

  // 컨텐츠 삭제
  deleteContent: async (tvId, contentId) => {
    try {
      const response = await client.delete(
        `/api/tvs/${tvId}/contents/${contentId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "컨텐츠 삭제에 실패했습니다."
      );
    }
  },

  // 컨텐츠 순서 변경
  reorderContents: async (tvId, contentIds) => {
    try {
      const response = await client.put(`/api/tvs/${tvId}/contents/reorder`, {
        contentIds,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "컨텐츠 순서 변경에 실패했습니다."
      );
    }
  },
};

// 음악 관련 API
export const musicApi = {
  // 음악 목록 조회
  getMusic: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}/music`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 목록 조회에 실패했습니다."
      );
    }
  },

  // 음악 추가
  addMusic: async (tvId, musicData) => {
    try {
      const response = await client.post(`/api/tvs/${tvId}/music`, musicData);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 추가에 실패했습니다."
      );
    }
  },

  // 음악 삭제
  removeMusic: async (tvId, musicId) => {
    try {
      const response = await client.delete(`/api/tvs/${tvId}/music/${musicId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 삭제에 실패했습니다."
      );
    }
  },

  // 음악 설정 조회
  getMusicSettings: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}/music/settings`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 설정 조회에 실패했습니다."
      );
    }
  },

  // 음악 설정 수정
  updateMusicSettings: async (tvId, settings) => {
    try {
      const response = await client.put(
        `/api/tvs/${tvId}/music/settings`,
        settings
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 설정 수정에 실패했습니다."
      );
    }
  },
};

// 파일 업로드 관련 API
export const fileApi = {
  // 이미지 업로드
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await client.post("/api/files/upload/image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "이미지 업로드에 실패했습니다."
      );
    }
  },

  // 동영상 업로드
  uploadVideo: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await client.post("/api/files/upload/video", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "동영상 업로드에 실패했습니다."
      );
    }
  },

  // 음악 파일 업로드
  uploadMusic: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await client.post("/api/files/upload/music", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "음악 파일 업로드에 실패했습니다."
      );
    }
  },
};

// PIN 인증 관련 API
export const pinApi = {
  // PIN 검증
  verifyPin: async (tvId, pin) => {
    try {
      const response = await client.post(`/api/tvs/${tvId}/pin/verify`, {
        pin,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "PIN 검증에 실패했습니다."
      );
    }
  },

  // PIN 설정 조회
  getPinSettings: async (tvId) => {
    try {
      const response = await client.get(`/api/tvs/${tvId}/pin`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "PIN 설정 조회에 실패했습니다."
      );
    }
  },

  // PIN 설정 수정
  updatePinSettings: async (tvId, pinSettings) => {
    try {
      const response = await client.put(`/api/tvs/${tvId}/pin`, pinSettings);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "PIN 설정 수정에 실패했습니다."
      );
    }
  },
};
